import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "../lib/api";
import { normalizeRole } from "../lib/features";

const AuthContext = createContext(null);
const DEV_BYPASS_USER = {
  id: "dev-local",
  name: "Admin Cliver",
  email: "admin@cliver.com",
  role: "admin",
  features: {},
};

function normalizeAuthUser(value) {
  if (!value || typeof value !== "object") return value;
  return {
    ...value,
    features: value.features && typeof value.features === "object" ? value.features : {},
  };
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchMe = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(normalizeAuthUser(data));
    } catch {
      const shouldBypassLogin = process.env.NODE_ENV === "development";
      setUser(shouldBypassLogin ? normalizeAuthUser(DEV_BYPASS_USER) : false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMe();
  }, [fetchMe]);

  const login = async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      if (data.access_token) localStorage.setItem("cliver_token", data.access_token);
      setUser(normalizeAuthUser(data));
      return { ok: true };
    } catch (e) {
      const shouldBypassLogin = process.env.NODE_ENV === "development";
      if (shouldBypassLogin) {
        setUser(normalizeAuthUser(DEV_BYPASS_USER));
        return { ok: true, bypassed: true };
      }
      return { ok: false, error: formatApiError(e.response?.data?.detail) || e.message, response: e.response?.data };
    }
  };

  const register = async (name, email, password) => {
    try {
      const { data } = await api.post("/auth/register", { name, email, password });
      if (data.access_token) localStorage.setItem("cliver_token", data.access_token);
      setUser(normalizeAuthUser(data));
      return { ok: true };
    } catch (e) {
      const shouldBypassLogin = process.env.NODE_ENV === "development";
      if (shouldBypassLogin) {
        setUser(normalizeAuthUser(DEV_BYPASS_USER));
        return { ok: true, bypassed: true };
      }
      return { ok: false, error: formatApiError(e.response?.data?.detail) || e.message, response: e.response?.data };
    }
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    localStorage.removeItem("cliver_token");
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, refresh: fetchMe }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
